import { useEffect, useState } from "react";
import { CalendarDays, ScrollText, PinIcon, Check, Eye, CheckCircle2, Clock, LogIn, LogOut } from "lucide-react";
import { EmployeeLayout } from "@/components/EmployeeLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DocumentViewDialog } from "@/components/DocumentViewDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EMPLOYEE_PORTAL, EMPLOYEE_TIMECLOCK } from "@/constants/testIds";
import { EMPLOYEE_VACATION_REQUEST, ANNOUNCEMENTS } from "@/constants/testIds/policies";
import { toast } from "sonner";

export default function EmployeeHome() {
  const { user, refreshUser } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [todayJornada, setTodayJornada] = useState(null);
  const [punching, setPunching] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  const loadEmployee = () => {
    if (user?.employee_id) api.get(`/employees/${user.employee_id}`).then(({ data }) => setEmployee(data));
  };
  const loadJornada = () => {
    if (user?.employee_id) api.get(`/employees/${user.employee_id}/timeclock`).then(({ data }) => setTodayJornada(data.today));
  };
  const loadAnnouncements = () => {
    api.get("/announcements/mine").then(({ data }) => setAnnouncements(data));
  };

  useEffect(() => {
    loadEmployee();
    loadJornada();
    loadAnnouncements();
  }, [user]);

  const getPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve({}), // permiso denegado o no disponible: el marcaje se registra igual, solo sin ubicación
        { timeout: 5000, maximumAge: 0 },
      );
    });

  const punch = async () => {
    setPunching(true);
    try {
      const coords = await getPosition();
      const { data } = await api.post(`/employees/${user.employee_id}/timeclock/punch`, coords);
      toast.success(data.message);
      loadJornada();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo registrar tu marcaje");
    } finally {
      setPunching(false);
    }
  };

  const handleSavePhone = async (e) => {
    e.preventDefault();
    setSavingPhone(true);
    try {
      const { data } = await api.patch(`/employees/${user.employee_id}/phone`, { phone: phoneInput });
      toast.success(data.message);
      setPhoneInput("");
      loadEmployee();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPhone(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", { new_password: newPassword });
      toast.success("Contraseña actualizada");
      await refreshUser();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirmVacation = async (id) => {
    try {
      const { data } = await api.post(`/vacation-requests/${id}/confirm`);
      toast.success(data.message);
      loadEmployee();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const declineVacation = async (id) => {
    try {
      const { data } = await api.post(`/vacation-requests/${id}/decline`);
      toast.success(data.message);
      loadEmployee();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/announcements/${id}/read`);
      loadAnnouncements();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const pendingPolicies = employee?.policies?.filter((p) => p.status === "pending") || [];
  const awaitingVacations = employee?.vacation_requests?.filter((r) => r.status === "awaiting_confirmation") || [];
  const pinnedAnnouncement = announcements[0];
  const otherAnnouncements = announcements.slice(1);

  return (
    <EmployeeLayout>
      <div data-testid={EMPLOYEE_TIMECLOCK.punchCard} className="bg-charcoal text-white rounded-2xl p-5 mb-5 shadow-[0_8px_30px_rgba(30,30,30,0.25)] flex items-center justify-between">
        <div>
          <p className="text-xs text-white/50 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Jornada de hoy</p>
          <p className="text-sm mt-1">
            {todayJornada?.entrada_time ? `Entrada ${todayJornada.entrada_time}` : "Sin marcar entrada"}
            {todayJornada?.entrada_location_status === "fuera_rango" && <span data-testid={EMPLOYEE_TIMECLOCK.locationBadgeOutOfRange} className="text-amber-300 font-semibold"> (fuera de rango)</span>}
            {todayJornada?.entrada_location_status === "sin_ubicacion" && <span data-testid={EMPLOYEE_TIMECLOCK.locationBadgeNoLocation} className="text-white/40"> (sin ubicación)</span>}
            {todayJornada?.salida_time ? ` · Salida ${todayJornada.salida_time}` : ""}
            {todayJornada?.salida_location_status === "fuera_rango" && <span className="text-amber-300 font-semibold"> (fuera de rango)</span>}
            {todayJornada?.salida_location_status === "sin_ubicacion" && <span className="text-white/40"> (sin ubicación)</span>}
          </p>
        </div>
        <Button
          data-testid={EMPLOYEE_TIMECLOCK.punchButton}
          onClick={punch}
          disabled={punching || todayJornada?.status === "completo"}
          className="bg-terracotta rounded-full shrink-0"
        >
          {todayJornada?.status === "en_turno" ? <LogOut className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
          {todayJornada?.status === "en_turno" ? "Marcar salida" : todayJornada?.status === "completo" ? "Jornada completa" : "Marcar entrada"}
        </Button>
      </div>

      {employee && !employee.phone && (
        <form onSubmit={handleSavePhone} data-testid={EMPLOYEE_PORTAL.missingPhoneBanner} className="bg-accent border border-terracotta/30 rounded-2xl p-5 mb-5">
          <p className="text-sm font-semibold text-charcoal mb-1">Completa tu perfil</p>
          <p className="text-xs text-charcoal/60 mb-2">Necesitamos un teléfono o WhatsApp de contacto para tu expediente. Agrégalo para dejar de ver este aviso.</p>
          <Input
            data-testid={EMPLOYEE_PORTAL.missingPhoneInput}
            type="tel"
            required
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="Ej. 55 1234 5678"
            className="bg-white mb-2"
          />
          <Button data-testid={EMPLOYEE_PORTAL.missingPhoneSubmit} type="submit" disabled={savingPhone} className="w-full bg-terracotta rounded-full">
            {savingPhone ? "Guardando..." : "Guardar teléfono"}
          </Button>
        </form>
      )}

      {pendingPolicies.length > 0 && (
        <div data-testid={EMPLOYEE_PORTAL.policyReminderPinnedCard} className="bg-terracotta text-white rounded-2xl p-4 mb-5 shadow-[0_8px_30px_rgba(217,130,43,0.3)]">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-white/15 px-2 py-0.5 rounded-full">
            <PinIcon className="w-3 h-3" /> Recordatorio automático
          </span>
          <p className="text-sm mt-2">Tienes {pendingPolicies.length} política(s) pendientes de confirmar. Revísalas abajo para continuar.</p>
        </div>
      )}

      {pinnedAnnouncement && (
        <div data-testid={ANNOUNCEMENTS.pinnedCard} className="bg-petroleum text-bone rounded-2xl p-4 mb-5 shadow-[0_8px_30px_rgba(14,79,79,0.25)] relative overflow-hidden">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-terracotta bg-bone/10 px-2 py-0.5 rounded-full">
            <PinIcon className="w-3 h-3" /> Fijado
          </span>
          <p className="text-sm mt-2">{pinnedAnnouncement.message}</p>
          {pinnedAnnouncement.read ? (
            <span className="inline-flex items-center gap-1 text-xs text-bone/60 mt-2"><Check className="w-3 h-3" /> Leído</span>
          ) : (
            <button data-testid={ANNOUNCEMENTS.pinnedMarkReadButton} onClick={() => markRead(pinnedAnnouncement.id)} className="text-xs font-semibold text-terracotta underline mt-2">
              Marcar como leído
            </button>
          )}
        </div>
      )}

      {user?.must_change_password && (
        <form onSubmit={handleChangePassword} data-testid={EMPLOYEE_PORTAL.changePasswordBanner} className="bg-accent border border-terracotta/30 rounded-2xl p-5 mb-5">
          <p className="text-sm font-semibold text-charcoal mb-2">Actualiza tu contraseña temporal</p>
          <Input data-testid={EMPLOYEE_PORTAL.newPasswordInput} type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nueva contraseña" className="bg-white mb-2" />
          <Button data-testid={EMPLOYEE_PORTAL.changePasswordSubmit} type="submit" className="w-full bg-terracotta rounded-full">Guardar</Button>
        </form>
      )}

      {awaitingVacations.length > 0 && awaitingVacations.map((r) => (
        <div key={r.id} data-testid={EMPLOYEE_VACATION_REQUEST.homeAwaitingBanner} className="bg-amber-50 border border-amber-300 rounded-2xl p-4 mb-5">
          <p className="text-sm font-semibold text-amber-800">Tu empresa te asignó vacaciones — confirma para continuar</p>
          <p className="text-sm text-charcoal/70 mt-1">{r.start_date} → {r.end_date} ({r.days_requested} días)</p>
          <div className="flex gap-2 mt-3">
            <Button data-testid={EMPLOYEE_VACATION_REQUEST.homeConfirmButton} size="sm" onClick={() => confirmVacation(r.id)} className="bg-petroleum rounded-full flex-1">Confirmar</Button>
            <Button data-testid={EMPLOYEE_VACATION_REQUEST.homeDeclineButton} size="sm" onClick={() => declineVacation(r.id)} variant="outline" className="rounded-full flex-1">Rechazar</Button>
          </div>
        </div>
      ))}

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
          <ScrollText className="w-5 h-5 text-petroleum mb-2" strokeWidth={1.5} />
          <p className="text-2xl font-heading font-bold text-charcoal">{pendingPolicies.length}</p>
          <p className="text-xs text-charcoal/60">Políticas pendientes</p>
        </div>
        <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
          <CalendarDays className="w-5 h-5 text-terracotta mb-2" strokeWidth={1.5} />
          <p className="text-2xl font-heading font-bold text-charcoal" data-testid={EMPLOYEE_PORTAL.vacationBalance}>{employee?.vacation_balance ?? "–"}</p>
          <p className="text-xs text-charcoal/60">Días de vacaciones</p>
        </div>
      </div>

      {employee?.policies?.length > 0 && (
        <div className="mb-5">
          <p className="text-sm font-semibold text-charcoal mb-2">Mis políticas</p>
          {employee.policies.map((p) => (
            <PolicyCard key={p.assignment_id} policy={p} employeeId={user.employee_id} onDone={loadEmployee} />
          ))}
        </div>
      )}

      {otherAnnouncements.length > 0 && (
        <div className="mb-5">
          <p className="text-sm font-semibold text-charcoal mb-2 flex items-center gap-1"><PinIcon className="w-3.5 h-3.5" /> Avisos anteriores</p>
          <div className="grid grid-cols-1 gap-2">
            {otherAnnouncements.map((a) => (
              <div
                key={a.id}
                data-testid={ANNOUNCEMENTS.postIt}
                className={`rounded-xl p-4 shadow-sm rotate-[-0.4deg] ${a.read ? "bg-accent/40 opacity-70" : "bg-[#FFF3C4] border border-amber-300"}`}
              >
                <p className="text-sm text-charcoal">{a.message}</p>
                {a.read ? (
                  <span className="inline-flex items-center gap-1 text-xs text-charcoal/50 mt-2"><Check className="w-3 h-3" /> Leído</span>
                ) : (
                  <button data-testid={`${ANNOUNCEMENTS.markReadButton}-${a.id}`} onClick={() => markRead(a.id)} className="text-xs font-semibold text-terracotta underline mt-2">
                    Marcar como leído
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </EmployeeLayout>
  );
}

function PolicyCard({ policy, employeeId, onDone }) {
  const [viewing, setViewing] = useState(false);
  const [viewed, setViewed] = useState(false);
  const isPending = policy.status === "pending";

  const acknowledge = async () => {
    try {
      await api.post(`/employees/${employeeId}/policies/${policy.assignment_id}/acknowledge`);
      toast.success("Política confirmada");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div
      data-testid={isPending ? EMPLOYEE_PORTAL.pendingPolicyCard : EMPLOYEE_PORTAL.acknowledgedPolicyCard}
      className={`bg-card border rounded-2xl p-4 mb-2 ${isPending ? "border-terracotta/30" : "border-petroleum/10"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium text-charcoal">{policy.title}</p>
          {policy.article_ref && <p className="text-xs font-mono-legal text-petroleum/70 mt-1">{policy.article_ref}</p>}
        </div>
        {!isPending && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button
          data-testid={`${EMPLOYEE_PORTAL.pendingPolicyViewButton}-${policy.assignment_id}`}
          onClick={() => setViewing(true)}
          className="text-xs font-semibold text-petroleum flex items-center gap-1 underline"
        >
          <Eye className="w-3 h-3" /> Ver documento
        </button>
      </div>
      {isPending && (
        <Button
          data-testid={EMPLOYEE_PORTAL.acknowledgeButton}
          onClick={acknowledge}
          disabled={!viewed}
          title={!viewed ? "Primero abre y lee el documento completo" : ""}
          size="sm"
          className="mt-2 w-full bg-petroleum rounded-full disabled:opacity-40"
        >
          He leído y confirmo
        </Button>
      )}
      {!isPending && policy.manual && (
        <p className="text-xs text-charcoal/50 mt-2">Confirmada manualmente por RH{policy.manual_note ? `: "${policy.manual_note}"` : ""}.</p>
      )}
      <DocumentViewDialog
        open={viewing}
        onOpenChange={setViewing}
        fetchDocUrl={`/employees/${employeeId}/policies/${policy.assignment_id}/document`}
        pdfUrl={`/employees/${employeeId}/policies/${policy.assignment_id}/pdf`}
        onViewed={() => setViewed(true)}
      />
    </div>
  );
}
